import React from "react";
import { motion } from "framer-motion";

import omHero from "./images1/om/01-smart-om-biogas-cbg.png";
import rbhmLogo from "./images1/om/02-rbhm-logo.png";
import adminLogin from "./images1/om/03-rbhm-admin-login.png";
import userLogin from "./images1/om/04-rbhm-user-login.png";
import alertGreen from "./images1/om/05-rbhm-alerts-green.png";
import alertYellow from "./images1/om/06-rbhm-alerts-yellow.png";
import alertRed from "./images1/om/07-rbhm-alerts-red.png";

/* =========================================================
   COMMON COMPONENTS
========================================================= */

const SectionTitle = ({ children, light = false }) => (
  <h2
    className={`text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-8 ${
      light ? "text-white" : "text-emerald-950"
    }`}
  >
    {children}
  </h2>
);

const SubTitle = ({ children, light = false }) => (
  <h3
    className={`text-xl md:text-2xl font-bold leading-relaxed mb-5 ${
      light ? "text-white" : "text-emerald-900"
    }`}
  >
    {children}
  </h3>
);

const BodyText = ({
  children,
  light = false,
  className = "",
}) => (
  <p
    className={`text-base md:text-lg leading-relaxed ${
      light ? "text-white/90" : "text-gray-700"
    } ${className}`}
  >
    {children}
  </p>
);

/*
  Updated:
  - Supports normal lists
  - Supports 3-column lists
  - Keeps all original bullet points
*/
const BulletList = ({
  items,
  light = false,
  columns = false,
}) => (
  <ul
    className={`
      list-disc pl-7
      ${
        columns
          ? "grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-2"
          : "space-y-2"
      }
      text-base md:text-lg
      leading-relaxed
      ${light ? "text-white/90" : "text-gray-700"}
    `}
  >
    {items.map((item, index) => (
      <li key={index}>{item}</li>
    ))}
  </ul>
);

const OMCard = ({ children }) => (
  <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100">
    {children}
  </div>
);

/* =========================================================
   PAGE
========================================================= */

const SmartOMBiogas = () => {
  return (
    <div className="bg-white text-gray-800">

      {/* =========================================================
          1. HERO
      ========================================================= */}

      <section className="bg-emerald-950 px-6 py-16 md:py-20">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-5xl"
          >
            <p className="text-amber-400 text-sm md:text-base font-semibold tracking-[0.22em] uppercase mb-5">
              SMART O&M FOR BIOGAS & CBG PLANTS
            </p>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5">
              From Early Warning to Reliable Plant Performance
            </h1>

            <h2 className="text-xl md:text-2xl lg:text-3xl text-amber-400 leading-relaxed font-semibold mb-6">
              RBHM-Powered Operations & Maintenance for Reliable, Profitable Plants
            </h2>

            <p className="text-lg md:text-xl text-white/90 leading-relaxed">
              Let the Promoter Own the Business. Let Renvitek Help Keep the Plant Running.
            </p>
          </motion.div>
        </div>
      </section>

      {/* =========================================================
          2. INTRODUCTION
      ========================================================= */}

      <section className="py-16 md:py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">

            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <BodyText className="mb-6">
                India’s Biogas and CBG industry is growing rapidly, but the availability of experienced
                bioprocess and plant-operation professionals remains a major challenge. RENVITEK
                Engineers LLP bridges this technical gap by providing specialised O&M and technical
                support for Biogas and CBG plants across India.
              </BodyText>

              <BodyText className="mb-6">
                Powered by our proprietary RBHM (Real-Time Bio-Digester Health Monitoring)
                technology, our approach integrates biological process intelligence, plant monitoring,
                preventive and predictive maintenance, technical troubleshooting, operator training, SOP
                development and performance reporting.
              </BodyText>

              <SubTitle>
                Our philosophy is simple:
              </SubTitle>

              <p className="text-xl md:text-2xl font-bold text-emerald-900 leading-relaxed mb-6">
                Detect Early → Diagnose Correctly → Act Promptly → Prevent Downtime
              </p>

              <BodyText>
                We aim to help plant owners achieve stable production, higher plant availability, reliable
                CBG output and improved operating economics, while reducing avoidable operational
                stress.
              </BodyText>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="bg-gray-100 rounded-2xl p-4 shadow-xl"
            >
              <img
                src={omHero}
                alt="Smart O&M for Biogas and CBG Plants"
                className="w-full rounded-xl object-contain"
              />
            </motion.div>

          </div>
        </div>
      </section>

      {/* =========================================================
          3. OUR VISION
      ========================================================= */}

      <section className="bg-emerald-50 py-16 md:py-20 px-6">
        <div className="max-w-7xl mx-auto">

          <SectionTitle>
            Our Vision
          </SectionTitle>

          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border-l-4 border-amber-400">

            <p className="text-xl md:text-2xl font-semibold text-emerald-900 leading-relaxed mb-6">
              “Every CBG plant in India should run reliably, consistently and profitably”.
            </p>

            <BodyText>
              We want promoters to focus on their business and returns, while Renvitek provides the
              technical expertise and support needed to keep their plant performing.
            </BodyText>

          </div>
        </div>
      </section>

      {/* =========================================================
          4. WHY RENVITEK O&M?
      ========================================================= */}

      <section className="py-16 md:py-20 px-6">
        <div className="max-w-7xl mx-auto">

          <SectionTitle>
            WHY RENVITEK O&M?
          </SectionTitle>

          <OMCard>

            <BodyText className="mb-5">
              Traditional O&M often becomes reactive:
            </BodyText>

            <p className="text-xl md:text-2xl font-semibold text-emerald-900 mb-7">
              Problem → Breakdown → Repair → Production Loss
            </p>

            <BodyText className="mb-5">
              Renvitek's RBHM-based approach changes this to:
            </BodyText>

            <p className="text-xl md:text-2xl font-semibold text-emerald-900 mb-7">
              OBSERVE → ANALYSE → PREDICT → ACT → VERIFY → IMPROVE
            </p>

            <BodyText className="mb-5">
              RBHM continuously combines plant operating data, laboratory information and historical
              process trends to identify biological and equipment deviations at an early stage.
            </BodyText>

            <BodyText>
              This allows the plant team to move from reactive maintenance to proactive and
              predictive O&M.
            </BodyText>

          </OMCard>
        </div>
      </section>

      {/* =========================================================
          5. OUR RBHM-BASED O&M APPROACH
      ========================================================= */}

      <section className="bg-gray-100 py-16 md:py-20 px-6">
        <div className="max-w-7xl mx-auto">

          <SectionTitle>
            OUR RBHM-BASED O&M APPROACH
          </SectionTitle>

          {/* OBSERVE */}

          <div className="mb-14">

            <SubTitle>
              1. OBSERVE
            </SubTitle>

            <BodyText className="mb-5">
              Continuous monitoring of critical plant and process parameters.
            </BodyText>

            <BodyText className="mb-5">
              Monitoring includes:
            </BodyText>

            <BulletList
              columns
              items={[
                "Feedstock quantity and quality",
                "TS and VS",
                "Feed preparation",
                "Feeding rate and frequency",
                "OLR",
                "HRT and SRT",
                "Digester temperature",
                "BGP",
                "BMP",
                "TAN",
                "Biogas Yield",
                "pH",
                "VFA",
                "Alkalinity",
                "FOS/TAC",
                "IA/PA",
                "BA/TA",
                "Biogas production",
                "Methane concentration",
                "H₂S",
                "Gas pressure",
                "Digestate flow",
                "Equipment status",
                "CBG production Yield (Kg-CBG/Ton FS)",
                "Specific Energy Consumption (SEC)",
                "Downtime",
                "KPIs",
                "PHI",
              ]}
            />

            <BodyText className="mt-6">
              RBHM evaluates both current operating conditions and historical trends, helping
              identify developing problems rather than simply recording present values.
            </BodyText>

          </div>

          {/* ANALYSE */}

          <div className="mb-14">

            <SubTitle>
              2. ANALYSE
            </SubTitle>

            <BodyText className="mb-5">
              Plant data is converted into meaningful technical information.
            </BodyText>

            <BodyText className="mb-5">
              Our technical team analyses:
            </BodyText>

            <BulletList
              items={[
                "Biological stability",
                "Feedstock variability",
                "Digester loading",
                "Gas production trends",
                "Equipment performance",
                "Gas purification performance",
                "Energy consumption",
                "Maintenance requirements",
                "Methane recovery",
                "Raw Biogas composition",
                "Production deviations",
                "Emerging operational risks",
              ]}
            />

            <BodyText className="mt-6">
              The objective is to identify what changed, why it changed and what action is required.
            </BodyText>

          </div>

          {/* PREDICT */}

          <div className="mb-14">

            <SubTitle>
              3. PREDICT
            </SubTitle>

            <p className="text-xl font-semibold text-emerald-900 mb-5">
              Early Warning Before Production Loss
            </p>

            <BodyText className="mb-5">
              RBHM classifies plant conditions into four operating levels:
            </BodyText>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-7">

              <div className="bg-white rounded-xl p-6 shadow">
                <p className="text-3xl mb-2">🟢 GREEN</p>
                <p className="text-gray-700">
                  Stable operating condition
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow">
                <p className="text-3xl mb-2">🟠 ORANGE</p>
                <p className="text-gray-700">
                  Process or equipment risk requiring intervention
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow">
                <p className="text-3xl mb-2">🟡 YELLOW</p>
                <p className="text-gray-700">
                  Early deviation requiring attention
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow">
                <p className="text-3xl mb-2">🔴 RED</p>
                <p className="text-gray-700">
                  Critical condition requiring immediate escalation
                </p>
              </div>

            </div>

            <BodyText>
              The system combines operating data, biological indicators, trends, feedstock behaviour
              and equipment condition to support timely corrective action.
            </BodyText>

          </div>

          {/* ACT */}

          <div>

            <SubTitle>
              4. ACT
            </SubTitle>

            <BodyText className="mb-5">
              Once a deviation is identified, Renvitek supports the plant team with:
            </BodyText>

            <BulletList
              items={[
                "Corrective-action recommendations",
                "Feeding and OLR adjustment",
                "Biological stabilisation",
                "Feedstock management",
                "Gas treatment optimisation",
                "Equipment inspection",
                "Troubleshooting",
                "Root Cause Analysis",
                "Preventive maintenance",
                "Critical spare recommendations",
                "Process optimisation",
                "Emergency technical escalation",
              ]}
            />

            <BodyText className="mt-6">
              The actual level of execution depends on the O&M service module selected by the plant
              owner.
            </BodyText>

          </div>

        </div>
      </section>

      {/* =========================================================
          6. THREE O&M SERVICE MODELS
      ========================================================= */}

      <section className="py-16 md:py-20 px-6">
        <div className="max-w-7xl mx-auto">

          <SectionTitle>
            THREE O&M SERVICE MODELS
          </SectionTitle>

          <BodyText className="mb-12">
            Renvitek offers three flexible O&M models so that every promoter can select the
            appropriate level of operational involvement.
          </BodyText>

          {/* MODULE A */}

          <div className="bg-emerald-50 rounded-2xl p-6 md:p-10 mb-12">

            <p className="text-amber-500 font-bold text-lg tracking-wide mb-2">
              MODULE A
            </p>

            <h3 className="text-2xl md:text-3xl font-bold text-emerald-950 mb-7">
              OWNER-CENTRIC / RENVITEK-MANAGED O&M
            </h3>

            <SubTitle>
              Concept
            </SubTitle>

            <BodyText className="mb-7">
              Under Module A, the Promoter retains ownership and strategic control, while Renvitek
              takes responsibility for day-to-day technical operation and maintenance of the plant.
            </BodyText>

            <div className="grid lg:grid-cols-2 gap-8">

              <OMCard>
                <SubTitle>
                  Promoter Scope
                </SubTitle>

                <BodyText className="mb-5">
                  The Promoter will primarily provide:
                </BodyText>

                <BulletList
                  items={[
                    "Strategic and commercial decisions",
                    "Plant Manager / designated management representative",
                    "Feedstock availability and supply arrangements",
                    "Approval/provision of consumables and critical spares",
                    "Product dispatch/commercial arrangements",
                    "Statutory and financial obligations specifically retained by the owner",
                  ]}
                />
              </OMCard>

              <OMCard>
                <SubTitle>
                  Renvitek Scope
                </SubTitle>

                <BodyText className="mb-5">
                  Renvitek will provide:
                </BodyText>

                <BulletList
                  items={[
                    "RBHM platform and continuous plant-health monitoring",
                    "Plant operations and maintenance management",
                    "Process and biological management",
                    "Technical supervision",
                    "Preventive and predictive maintenance planning",
                    "Breakdown troubleshooting and technical coordination",
                    "Daily operational monitoring",
                    "Laboratory/process monitoring",
                    "Equipment performance monitoring",
                    "Shift/operator guidance and training",
                    "Production and performance reporting",
                    "Critical-spares recommendations",
                    "Root Cause Analysis of major failures",
                    "Monthly plant performance review",
                    "Process optimisation for improved gas yield and plant availability",
                  ]}
                />
              </OMCard>

            </div>

            <div className="mt-8">

              <SubTitle>
                Commercial Principle
              </SubTitle>

              <BodyText className="mb-5">
                Renvitek manages the technical and operational execution, while the Promoter retains
                control over commercial decisions, budgets and strategic matters.
              </BodyText>

              <BodyText>
                This is the closest model to a stress-free plant ownership model.
              </BodyText>

            </div>

          </div>

          {/* MODULE B */}

          <div className="bg-gray-100 rounded-2xl p-6 md:p-10 mb-12">

            <p className="text-amber-500 font-bold text-lg tracking-wide mb-2">
              MODULE B
            </p>

            <h3 className="text-2xl md:text-3xl font-bold text-emerald-950 mb-7">
              HYBRID O&M MODEL
            </h3>

            <SubTitle>
              Concept
            </SubTitle>

            <BodyText className="mb-7">
              The Promoter maintains its own operating team, while Renvitek becomes the technical
              backbone of the plant through RBHM, process engineering and specialist support.
            </BodyText>

            <div className="grid lg:grid-cols-2 gap-8">

              <OMCard>

                <SubTitle>
                  Promoter Scope
                </SubTitle>

                <BodyText className="mb-5">
                  The Promoter will manage:
                </BodyText>

                <BulletList
                  items={[
                    "Feedstock procurement and supply",
                    "Plant Manager",
                    "Shift Managers / Supervisors",
                    "Operators and general plant manpower",
                    "Routine plant operations",
                  ]}
                />

              </OMCard>

              <OMCard>

                <SubTitle>
                  Renvitek Scope
                </SubTitle>

                <BodyText className="mb-5">
                  Renvitek will provide:
                </BodyText>

                <BulletList
                  items={[
                    "RBHM platform",
                    "Biological process monitoring",
                    "Technical advisory",
                    "Bioprocess engineering support",
                    "Laboratory guidance and interpretation",
                    "Consumables",
                    "Routine and approved spare parts",
                    "Product handling and dispatch",
                    "Routine housekeeping",
                    "Day-to-day operational decisions",
                    "Digester health assessment",
                    "Early-warning identification",
                    "Process optimisation",
                    "Troubleshooting support",
                    "Preventive-maintenance advisory",
                    "Major breakdown technical support",
                    "Root Cause Analysis",
                    "Performance review",
                    "Monthly technical audit",
                    "Operator and supervisor training",
                    "Recommendations for improving plant efficiency and profitability",
                  ]}
                />

              </OMCard>

            </div>

            <div className="mt-8">

              <SubTitle>
                Key Advantage
              </SubTitle>

              <BodyText className="mb-5">
                The Promoter retains its own operating team, while Renvitek acts as the technical
                control and support layer.
              </BodyText>

              <BodyText>
                This model is particularly suitable where the promoter already has manpower but wants
                to avoid depending entirely on in-house technical expertise.
              </BodyText>

            </div>

          </div>

          {/* MODULE C */}

          <div className="bg-emerald-50 rounded-2xl p-6 md:p-10">

            <p className="text-amber-500 font-bold text-lg tracking-wide mb-2">
              MODULE C
            </p>

            <h3 className="text-2xl md:text-3xl font-bold text-emerald-950 mb-7">
              RBHM TECHNICAL ASSURANCE
            </h3>

            <SubTitle>
              Concept
            </SubTitle>

            <BodyText className="mb-7">
              This is the lightest and most scalable Renvitek service model.
              The Promoter independently operates the plant, while Renvitek provides RBHM-based
              technical intelligence and specialist advisory support.
            </BodyText>

            <div className="grid lg:grid-cols-2 gap-8">

              <OMCard>

                <SubTitle>
                  Promoter Scope
                </SubTitle>

                <BodyText className="mb-5">
                  The Promoter will be responsible for:
                </BodyText>

                <BulletList
                  items={[
                    "Complete plant operation",
                    "Plant Manager and operating manpower",
                    "Feedstock procurement and quality",
                    "Daily feeding and process operation",
                    "Consumables and spares",
                    "Routine maintenance",
                    "Breakdown maintenance",
                    "Product handling and dispatch",
                    "Statutory compliance",
                    "Safety management",
                    "Commercial decisions",
                  ]}
                />

              </OMCard>

              <OMCard>

                <SubTitle>
                  Renvitek Scope
                </SubTitle>

                <BodyText className="mb-5">
                  Renvitek will provide:
                </BodyText>

                <BulletList
                  items={[
                    "RBHM platform",
                    "Digital plant-health monitoring",
                    "Biological Stress Index / plant-health assessment",
                    "Early-warning alerts",
                    "Trend analysis",
                    "Technical recommendations",
                    "Remote technical consultation",
                    "Periodic technical review",
                    "Process optimisation recommendations",
                    "Escalation support for abnormal conditions",
                  ]}
                />

              </OMCard>

            </div>

            <div className="mt-8 bg-white rounded-xl p-6 border-l-4 border-amber-400">

              <SubTitle>
                Important Boundary
              </SubTitle>

              <BodyText>
                Renvitek provides technical intelligence and recommendations. The Promoter
                remains responsible for execution and operation.
              </BodyText>

            </div>

          </div>

        </div>
      </section>

      {/* =========================================================
          7. RBHM-POWERED O&M : EXECUTION STRATEGY
      ========================================================= */}

      <section className="bg-emerald-950 py-16 md:py-20 px-6">
        <div className="max-w-7xl mx-auto">

          <SectionTitle light>
            RBHM-POWERED O&M : EXECUTION STRATEGY
          </SectionTitle>

          {/* FEEDSTOCK */}

          <div className="bg-white rounded-2xl p-6 md:p-10 mb-10">

            <SubTitle>
               FEEDSTOCK & BIOLOGICAL PROCESS MANAGEMENT
            </SubTitle>

            <p className="text-xl font-semibold text-emerald-900 mb-5">
              A Healthy Digester Starts with the Right Feed
            </p>

            <BodyText className="mb-5">
              Feedstock variability is one of the most important factors affecting anaerobic digestion
              performance.
            </BodyText>

            <BodyText className="mb-5">
              Our O&M approach focuses on:
            </BodyText>

            <BulletList
              items={[
                "Feedstock quantity",
                "Feedstock quality",
                "Moisture",
                "TS",
                "VS",
                "Contamination",
                "Particle size",
                "Feedstock variability",
                "Slurry preparation",
                "Water requirement",
                "Feeding frequency",
                "Feeding quantity",
                "OLR management",
                "MAC Water addition calculations",
              ]}
            />

            <BodyText className="mt-6">
              RBHM supports dynamic feeding and loading decisions based on actual feedstock
              characteristics, digester condition, historical production and biological behaviour.
            </BodyText>

          </div>

          {/* DIGESTER HEALTH */}

          <div className="bg-white rounded-2xl p-6 md:p-10 mb-10">

            <SubTitle>
               DIGESTER HEALTH MANAGEMENT
            </SubTitle>

            <p className="text-xl font-semibold text-emerald-900 mb-5">
              Protect the Biology. Protect the Plant.
            </p>

            <BodyText className="mb-5">
              Anaerobic digestion is a biological process. A mechanical system can often be repaired
              quickly, but biological instability can affect production for an extended period.
            </BodyText>

            <BodyText className="mb-5">
              Renvitek therefore monitors and interprets critical biological indicators including:
            </BodyText>

            <p className="text-lg md:text-xl font-semibold text-emerald-900 leading-relaxed mb-5">
              pH | Temperature | VFA | Alkalinity | FOS/TAC | IA/PA | BA/TA | TS | VS | OLR |
              HRT | SRT
            </p>

            <BodyText>
              This enables early identification of biological stress and supports timely stabilisation
              measures.
            </BodyText>

          </div>

          {/* PREVENTIVE */}

          <div className="bg-white rounded-2xl p-6 md:p-10 mb-10">

            <SubTitle>
               PREVENTIVE & PREDICTIVE MAINTENANCE
            </SubTitle>

            <p className="text-xl font-semibold text-emerald-900 mb-5">
              Maintenance Before Failure
            </p>

            <BodyText className="mb-5">
              Renvitek develops structured maintenance schedules covering:
            </BodyText>

            <BulletList
              items={[
                "Feed preparation equipment",
                "Pumps",
                "Mixers",
                "Digesters",
                "Gas handling systems",
                "H₂S removal systems",
                "Moisture removal systems",
                "Gas purification systems",
                "Compressors",
                "CBG purification systems",
                "Electrical systems",
                "PLC/VFD systems",
                "Sensors and instrumentation",
                "Utilities",
              ]}
            />

            <BodyText className="mt-6">
              Inspection, servicing, calibration, lubrication and wear-part replacement are planned in
              advance to improve equipment reliability and plant availability.
            </BodyText>

          </div>

          {/* ZERO SHUTDOWN */}

          <div className="bg-white rounded-2xl p-6 md:p-10 mb-10">

            <SubTitle>
               ZERO AVOIDABLE UNPLANNED SHUTDOWN
            </SubTitle>

            <p className="text-xl md:text-2xl font-bold text-emerald-900 mb-5">
              MONITOR → DETECT → DIAGNOSE → ACT → PREVENT → RESTORE
            </p>

            <BodyText className="mb-5">
              Our objective is not simply to repair failures. It is to reduce the probability of avoidable
              failures occurring in the first place.
            </BodyText>

            <BodyText className="mb-5">
              Renvitek maintains a Critical Spare Parts Register with:
            </BodyText>

            <div className="space-y-3 mb-6">
              <p className="text-lg text-gray-700">
                Category A - Critical
              </p>

              <p className="text-lg text-gray-700">
                Category B - Important
              </p>

              <p className="text-lg text-gray-700">
                Category C - Routine
              </p>
            </div>

            <BodyText>
              Spare requirements are planned using operational and maintenance data to reduce
              unnecessary inventory while protecting plant availability.
            </BodyText>

          </div>

          {/* REMOTE MONITORING */}

          <div className="bg-white rounded-2xl p-6 md:p-10 mb-10">

            <SubTitle>
               24 × 7 REMOTE TECHNICAL MONITORING
            </SubTitle>

            <BodyText className="mb-5">
              Subject to the selected service package and connectivity, Renvitek's technical team can
              remotely monitor plant data through RBHM.
            </BodyText>

            <p className="text-lg md:text-xl font-semibold text-emerald-900 leading-relaxed mb-5">
              Plant → RBHM → Dashboard → Alert → Technical Team → Site Operator →
              Corrective Action → Confirmation
            </p>

            <BodyText>
              This architecture allows centralised technical support for multiple plants and provides the
              foundation for scalable O&M management.
            </BodyText>

          </div>

          {/* CENTRAL O&M */}

          <div className="bg-white rounded-2xl p-6 md:p-10 mb-10">

            <SubTitle>
               CENTRAL O&M MONITORING
            </SubTitle>

            <p className="text-xl font-semibold text-emerald-900 mb-5">
              One Technical Intelligence Layer. Multiple Plants.
            </p>

            <BodyText className="mb-5">
              Renvitek can establish a centralised Biogas & CBG O&M Monitoring Centre for
              monitoring multiple plants through a common dashboard.
            </BodyText>

            <BodyText className="mb-5">
              The monitoring system can provide visibility of:
            </BodyText>

            <BulletList
              items={[
                "Plant operating status",
                "Biological health",
                "Biogas production",
                "Equipment status",
                "Alerts",
                "Downtime",
                "Methane",
                "CBG production",
                "Maintenance status",
                "Performance trends",
              ]}
            />

            <BodyText className="mt-6">
              This enables a technology-driven O&M organisation rather than a purely site-by-site
              service model.
            </BodyText>

          </div>

          {/* LABORATORY */}

          <div className="bg-white rounded-2xl p-6 md:p-10 mb-10">

            <SubTitle>
               LABORATORY & BIOANALYTICAL SUPPORT
            </SubTitle>

            <p className="text-xl font-semibold text-emerald-900 mb-5">
              Data + Biology + Engineering
            </p>

            <BodyText className="mb-5">
              Renvitek integrates laboratory information with real-time operating data and historical
              process behaviour.
            </BodyText>

            <BodyText className="mb-5">
              Depending on plant size, feedstock variability and biological risk, testing may include:
            </BodyText>

            <p className="text-lg text-gray-700 leading-relaxed mb-5">
              pH, TS, VS, VFA, Alkalinity, FOS/TAC, IA/PA, BA/TA, Ammonia, Feedstock
              characteristics, Digestate characteristics, Fertilizer characteristics, CBG and Raw Biogas
              Composition
            </p>

            <BodyText>
              This provides a stronger technical basis for process optimisation and early intervention.
            </BodyText>

          </div>

          {/* EXISTING PLANT */}

          <div className="bg-white rounded-2xl p-6 md:p-10 mb-10">

            <SubTitle>
               EXISTING PLANT TAKEOVER
            </SubTitle>

            <p className="text-xl font-semibold text-emerald-900 mb-5">
              Stabilise First. Optimise Next.
            </p>

            <BodyText className="mb-5">
              For an existing Biogas or CBG plant, Renvitek begins with a structured Pre-O&M
              Assessment.
            </BodyText>

            <BodyText className="mb-5">
              Our assessment includes:
            </BodyText>

            <ol className="list-decimal pl-7 space-y-2 text-base md:text-lg text-gray-700 leading-relaxed">
              <li>Documentation Audit</li>
              <li>Physical Plant Inspection</li>
              <li>Biological Assessment</li>
              <li>Equipment Health Assessment</li>
              <li>Performance Baseline</li>
              <li>30/60/90-Day Stabilisation Plan</li>
            </ol>

            <BodyText className="mt-6">
              The performance baseline is established before assuming full O&M responsibility and
              before committing to performance KPIs.
            </BodyText>

          </div>

        </div>
      </section>

      {/* =========================================================
          8. DAILY / WEEKLY / MONTHLY REPORTS
      ========================================================= */}

      <section className="bg-emerald-50 py-16 md:py-20 px-6">
        <div className="max-w-7xl mx-auto">

          <SectionTitle>
             DAILY, WEEKLY & MONTHLY PERFORMANCE MANAGEMENT & REPORTS
          </SectionTitle>

          <div className="grid md:grid-cols-3 gap-8">

            <OMCard>
              <SubTitle>Daily</SubTitle>

              <BulletList
                items={[
                  "Feedstock monitoring",
                  "Digester monitoring",
                  "Gas quality",
                  "Equipment status",
                  "Production",
                ]}
              />
            </OMCard>

            <OMCard>
              <SubTitle>Weekly</SubTitle>

              <BulletList
                items={[
                  "Production trends",
                  "Methane performance",
                  "Biological health",
                  "OLR",
                  "Equipment availability",
                ]}
              />
            </OMCard>

            <OMCard>
              <SubTitle>Monthly</SubTitle>

              <BulletList
                items={[
                  "Plant availability",
                  "CBG production efficiency",
                  "Methane recovery",
                  "Equipment availability",
                  "Maintenance activities",
                  "Corrective actions",
                  "Energy consumption",
                  "Downtime",
                  "Maintenance compliance",
                  "Corrective actions",
                  "Unplanned downtime",
                  "Preventive maintenance compliance",
                  "Plant performance",
                  "Improvement opportunities",
                ]}
              />
            </OMCard>

          </div>

        </div>
      </section>

      {/* =========================================================
          9. PERFORMANCE KPIs
      ========================================================= */}

      <section className="py-16 md:py-20 px-6">
        <div className="max-w-7xl mx-auto">

          <SectionTitle>
             PERFORMANCE KPIs
          </SectionTitle>

          <BodyText className="mb-8">
            Renvitek's O&M performance can be evaluated through measurable operational and
            business KPIs.
          </BodyText>

          <OMCard>

            <SubTitle>
              Key KPIs include:
            </SubTitle>

            <BulletList
              items={[
                "Plant Availability",
                "CBG Production",
                "Methane Concentration",
                "Methane Recovery",
                "Feedstock-to-Gas Conversion",
                "Specific Gas Yield",
                "Energy Consumption",
                "Equipment Availability",
                "Unplanned Downtime",
                "Preventive Maintenance Compliance",
                "Biological Stability",
                "Gas Quality",
                "Alarm Response",
                "Corrective-Action Closure",
                "Safety Performance",
              ]}
            />

            <BodyText className="mt-6">
              These KPIs provide the promoter with a clear view of plant health, reliability and
              operational performance.
            </BodyText>

          </OMCard>

        </div>
      </section>

      {/* =========================================================
          10. RENVITEK O&M ORGANISATION
      ========================================================= */}

      <section className="bg-gray-100 py-16 md:py-20 px-6">
        <div className="max-w-7xl mx-auto">

          <SectionTitle>
             RENVITEK O&M ORGANISATION
          </SectionTitle>

          <p className="text-xl md:text-2xl font-semibold text-emerald-900 mb-6">
            Local Execution + Centralised Technical Intelligence
          </p>

          <BodyText className="mb-10">
            Our O&M structure can combine experienced site manpower with centralised technical
            support.
          </BodyText>

          <div className="grid lg:grid-cols-2 gap-8">

            <OMCard>
              <SubTitle>Site Team</SubTitle>

              <BulletList
                items={[
                  "Plant / O&M Manager",
                  "Shift Engineer",
                  "Process Operator",
                  "Mechanical Technician",
                  "Electrical Technician",
                  "Utility / General Operators",
                ]}
              />
            </OMCard>

            <OMCard>
              <SubTitle>Central Technical Team</SubTitle>

              <BulletList
                items={[
                  "O&M Head",
                  "RBHM Technical / Process Team",
                  "Biological / Process Expert",
                  "Mechanical / Electrical Expert",
                  "Remote Monitoring Team",
                ]}
              />
            </OMCard>

          </div>

          <BodyText className="mt-8">
            This structure allows plants to receive both on-site execution capability and specialist
            technical support.
          </BodyText>

        </div>
      </section>

      {/* =========================================================
          11. OUR APPROACH TO PLANT OWNERS
      ========================================================= */}

      <section className="py-16 md:py-20 px-6">
        <div className="max-w-7xl mx-auto">

          <SectionTitle>
             OUR APPROACH TO PLANT OWNERS
          </SectionTitle>

          <p className="text-xl md:text-2xl font-semibold text-emerald-900 mb-6">
            Your investment. Your Plant. Your Profit.
          </p>

          <BodyText className="mb-6">
            Renvitek's O&M philosophy is designed around a simple principle:
          </BodyText>

          <p className="text-xl font-semibold text-emerald-900 mb-8">
            The promoter should focus on the business, not daily plant problems.
          </p>

          <BodyText className="mb-6">
            Our role is to provide the technical systems, monitoring, expertise and operational support
            required to keep the plant running reliably.
          </BodyText>

          <BodyText className="mb-5">
            We help plant owners move from:
          </BodyText>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">

            <div className="bg-emerald-50 rounded-xl p-5 text-center">
              <p className="font-semibold text-emerald-900">
                Reactive → Proactive
              </p>
            </div>

            <div className="bg-emerald-50 rounded-xl p-5 text-center">
              <p className="font-semibold text-emerald-900">
                Breakdown → Prevention
              </p>
            </div>

            <div className="bg-emerald-50 rounded-xl p-5 text-center">
              <p className="font-semibold text-emerald-900">
                Guesswork → Data
              </p>
            </div>

            <div className="bg-emerald-50 rounded-xl p-5 text-center">
              <p className="font-semibold text-emerald-900">
                Production Loss → Early Intervention
              </p>
            </div>

            <div className="bg-emerald-50 rounded-xl p-5 text-center">
              <p className="font-semibold text-emerald-900">
                Operational Stress → Technical Confidence
              </p>
            </div>

          </div>

          <BodyText>
            The value of our O&M service goes beyond manpower. It is designed around production
            continuity, biological stability, reduced downtime, asset utilisation, methane recovery,
            stable CBG production, predictive maintenance, technical visibility and revenue
            protection.
          </BodyText>

        </div>
      </section>

      {/* =========================================================
          12. WHY CHOOSE RENVITEK?
      ========================================================= */}

      <section className="bg-emerald-50 py-16 md:py-20 px-6">
        <div className="max-w-7xl mx-auto">

          <SectionTitle>
             WHY CHOOSE RENVITEK?
          </SectionTitle>

          <div className="overflow-x-auto">

            <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">

              <thead className="bg-emerald-950 text-white">
                <tr>
                  <th className="border border-gray-300 p-4 text-left">
                    Key Strength
                  </th>

                  <th className="border border-gray-300 p-4 text-left">
                    Description
                  </th>
                </tr>
              </thead>

              <tbody>

                <tr>
                  <td className="border border-gray-300 p-4 font-semibold text-emerald-900">
                    Biogas & CBG Process Expertise
                  </td>

                  <td className="border border-gray-300 p-4 text-gray-700">
                    Technical understanding of biological digestion and downstream gas systems.
                  </td>
                </tr>

                <tr>
                  <td className="border border-gray-300 p-4 font-semibold text-emerald-900">
                    RBHM Technology
                  </td>

                  <td className="border border-gray-300 p-4 text-gray-700">
                    Real-time plant and biological health intelligence.
                  </td>
                </tr>

                <tr>
                  <td className="border border-gray-300 p-4 font-semibold text-emerald-900">
                    Proactive O&M
                  </td>

                  <td className="border border-gray-300 p-4 text-gray-700">
                    Identify deviations before they become major operational problems.
                  </td>
                </tr>

                <tr>
                  <td className="border border-gray-300 p-4 font-semibold text-emerald-900">
                    Integrated Biology + Equipment Approach
                  </td>

                  <td className="border border-gray-300 p-4 text-gray-700">
                    Process performance and equipment reliability are evaluated together.
                  </td>
                </tr>

                <tr>
                  <td className="border border-gray-300 p-4 font-semibold text-emerald-900">
                    Data-Driven Decisions
                  </td>

                  <td className="border border-gray-300 p-4 text-gray-700">
                    Operational decisions supported by plant data, laboratory information and historical trends.
                  </td>
                </tr>

                <tr>
                  <td className="border border-gray-300 p-4 font-semibold text-emerald-900">
                    Flexible Service Models
                  </td>

                  <td className="border border-gray-300 p-4 text-gray-700">
                    Choose complete O&M, hybrid support or technical assurance.
                  </td>
                </tr>

                <tr>
                  <td className="border border-gray-300 p-4 font-semibold text-emerald-900">
                    Centralised Technical Support
                  </td>

                  <td className="border border-gray-300 p-4 text-gray-700">
                    Specialist expertise available across multiple plants.
                  </td>
                </tr>

                <tr>
                  <td className="border border-gray-300 p-4 font-semibold text-emerald-900">
                    Performance-Focused O&M
                  </td>

                  <td className="border border-gray-300 p-4 text-gray-700">
                    Focus on availability, production, reliability and operating economics.
                  </td>
                </tr>

              </tbody>

            </table>

          </div>

        </div>
      </section>

      {/* =========================================================
          13. WEB IMAGES - RBHM PLATFORM
      ========================================================= */}

      <section className="py-16 md:py-20 px-6">
        <div className="max-w-7xl mx-auto">

          <SectionTitle>
            RBHM-Powered O&M Platform: Admin & User Interface
          </SectionTitle>

          <div className="grid lg:grid-cols-2 gap-8">

            <div className="bg-gray-100 rounded-2xl p-5 shadow-lg">

              <img
                src={adminLogin}
                alt="RBHM Admin Portal Login"
                className="w-full h-[420px] object-contain rounded-xl"
              />

            </div>

            <div className="bg-gray-100 rounded-2xl p-5 shadow-lg">

              <img
                src={userLogin}
                alt="RBHM User Portal"
                className="w-full h-[420px] object-contain rounded-xl"
              />

            </div>

          </div>

          <div className="mt-10 flex justify-center">

            <img
              src={rbhmLogo}
              alt="RBHM Real-Time Bio-Digester Health Monitoring"
              className="max-w-md w-full object-contain"
            />

          </div>

        </div>
      </section>

      {/* =========================================================
          14. RBHM SMART ALERTS
      ========================================================= */}

      <section className="bg-gray-100 py-16 md:py-20 px-6">
        <div className="max-w-7xl mx-auto">

          <SectionTitle>
            RBHM Smart Alerts: Real-Time Plant Health Notifications
          </SectionTitle>

          <div className="grid md:grid-cols-3 gap-8">

            <div className="bg-white rounded-2xl p-5 shadow-lg">

              <img
                src={alertGreen}
                alt="RBHM Green Alert"
                className="w-full h-[380px] object-contain rounded-xl"
              />

            </div>

            <div className="bg-white rounded-2xl p-5 shadow-lg">

              <img
                src={alertYellow}
                alt="RBHM Yellow Alert"
                className="w-full h-[380px] object-contain rounded-xl"
              />

            </div>

            <div className="bg-white rounded-2xl p-5 shadow-lg">

              <img
                src={alertRed}
                alt="RBHM Red Alert"
                className="w-full h-[380px] object-contain rounded-xl"
              />

            </div>

          </div>

        </div>
      </section>

      {/* =========================================================
          15. LET'S BUILD A RELIABLE BIOGAS & CBG OPERATION
      ========================================================= */}

      <section className="bg-emerald-950 py-20 md:py-24 px-6">

        <div className="max-w-6xl mx-auto text-center">

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-10">
             LET'S BUILD A RELIABLE BIOGAS & CBG OPERATION
          </h2>

          <div className="bg-white/10 rounded-2xl p-8 md:p-10 text-left">

            <p className="text-xl md:text-2xl font-semibold text-amber-400 mb-6">
              Whether you are:
            </p>

            <ul className="list-disc pl-7 space-y-3 text-white/90 text-base md:text-lg leading-relaxed">

              <li>
                Planning a new Biogas or CBG plant
              </li>

              <li>
                Commissioning a new facility
              </li>

              <li>
                Operating an existing plant
              </li>

              <li>
                Facing unstable digester performance
              </li>

              <li>
                Experiencing frequent breakdowns
              </li>

              <li>
                Looking to reduce downtime
              </li>

              <li>
                Looking to improve CBG production
              </li>

              <li>
                Looking for technical assurance
              </li>

              <li>
                Looking to establish professional O&M systems
              </li>

            </ul>

            <p className="text-white/90 text-base md:text-lg leading-relaxed mt-8">
              Renvitek can design an O&M model around your plant, your team and your
              business objectives.
            </p>

          </div>

          <div className="mt-12">

            <p className="text-xl md:text-2xl font-semibold text-amber-400 mb-5">
              Talk to Renvitek about your O&M requirements
            </p>

            <p className="text-xl md:text-2xl font-bold text-white">
              RENVITEK ENGINEERS LLP
            </p>

            <p className="text-lg text-white/80 mt-2">
              Engineering the Sustainable Future
            </p>

          </div>

        </div>

      </section>

    </div>
  );
};

export default SmartOMBiogas;
